import React, { useState, useEffect, useCallback, useRef } from 'react';
import { acsAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  Router, Search, RefreshCw, Power, Wifi, Activity, Users,
  AlertTriangle, Download, Terminal, X, Signal, Clock,
} from 'lucide-react';

const fmtUptime = (s) => {
  if (!s) return '—';
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
};
const fmtTime = (t) => (t ? format(new Date(t), 'dd/MM/yy HH:mm:ss') : '—');

const OnlineBadge = ({ online }) => (
  <span className={`badge badge-${online ? 'success' : 'danger'}`}>
    <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', marginRight: 5, background: 'currentColor' }} />
    {online ? 'Online' : 'Offline'}
  </span>
);

// ─── WiFi tab ─────────────────────────────────────────────────────────────────
function WifiTab({ device, onDone }) {
  const [forms, setForms] = useState({});
  const [busy, setBusy] = useState(null);
  const nets = device.wifiFull || [];

  const apply = async (net) => {
    const f = forms[net.instance] || {};
    if (!f.ssid && !f.password) return toast.error('Enter a new SSID or password');
    if (f.password && (f.password.length < 8 || f.password.length > 63)) return toast.error('Password must be 8-63 characters');
    setBusy(net.instance);
    try {
      const { data } = await acsAPI.changeWifi(device.serial, {
        instance: net.instance, band: net.band, ssid: f.ssid || undefined, password: f.password || undefined,
      });
      if (data.data.completed) toast.success(`WiFi updated on ${net.band}GHz`);
      else toast(`Queued — applies when ONT reconnects`, { icon: '⏳' });
      setForms((x) => ({ ...x, [net.instance]: {} }));
      onDone();
    } catch (err) { toast.error(err.response?.data?.message || 'WiFi change failed'); }
    finally { setBusy(null); }
  };

  if (!nets.length) return <div className="empty-state"><Wifi size={28} /><h3>No WiFi data</h3><p>Refresh the device to read WLAN configuration.</p></div>;

  return (
    <div>
      {nets.map((net) => (
        <div key={net.instance} className="card" style={{ marginBottom: 12 }}>
          <div className="card-body" style={{ padding: 14 }}>
            <div className="flex justify-between items-center" style={{ marginBottom: 10 }}>
              <div>
                <span className={`badge badge-${net.band === '5' ? 'catv' : 'info'}`}>{net.band} GHz</span>
                <span style={{ marginLeft: 8, fontWeight: 600 }}>{net.ssid || '(no ssid)'}</span>
                <span className="text-muted text-small" style={{ marginLeft: 8 }}>
                  {net.enabled ? `Ch ${net.channel || '—'} · ${net.clients} client(s)` : 'Disabled'}
                </span>
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">New SSID (WiFi name)</label>
                <input className="form-control" placeholder={net.ssid} value={forms[net.instance]?.ssid || ''}
                  onChange={(e) => setForms((x) => ({ ...x, [net.instance]: { ...x[net.instance], ssid: e.target.value } }))} />
              </div>
              <div className="form-group">
                <label className="form-label">New WiFi Password</label>
                <input className="form-control" placeholder="8-63 characters" value={forms[net.instance]?.password || ''}
                  onChange={(e) => setForms((x) => ({ ...x, [net.instance]: { ...x[net.instance], password: e.target.value } }))} />
              </div>
            </div>
            <button className="btn btn-primary" disabled={busy === net.instance} onClick={() => apply(net)}>
              <Wifi size={14} /> {busy === net.instance ? 'Applying…' : `Apply to ${net.band}GHz`}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Advanced tab ─────────────────────────────────────────────────────────────
function AdvancedTab({ device, onDone, onClose }) {
  const [fwUrl, setFwUrl] = useState('');
  const [paramName, setParamName] = useState('');
  const [paramValue, setParamValue] = useState('');
  const [pingHost, setPingHost] = useState('8.8.8.8');
  const [pingResult, setPingResult] = useState(null);
  const [busy, setBusy] = useState('');

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const { data } = await fn();
      toast.success(okMsg || 'Done');
      return data;
    } catch (err) { toast.error(err.response?.data?.message || 'Action failed'); }
    finally { setBusy(''); }
  };

  const doPing = async () => {
    if (!pingHost) return;
    await run('ping', () => acsAPI.ping(device.serial, pingHost), 'Ping requested');
    setTimeout(async () => {
      try { const { data } = await acsAPI.getPingResults(device.serial); setPingResult(data.data); } catch { /* ignore */ }
    }, 4000);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body" style={{ padding: 14 }}>
          <h4 style={{ fontSize: '.85rem', marginBottom: 10 }}><Activity size={13} /> Ping Diagnostic (from ONT)</h4>
          <div className="flex" style={{ gap: 8 }}>
            <input className="form-control" style={{ maxWidth: 220 }} value={pingHost} onChange={(e) => setPingHost(e.target.value)} placeholder="Host e.g. 8.8.8.8" />
            <button className="btn btn-secondary" disabled={busy === 'ping'} onClick={doPing}>{busy === 'ping' ? 'Running…' : 'Ping'}</button>
          </div>
          {pingResult && (
            <p className="text-small" style={{ marginTop: 8 }}>
              State: <b>{pingResult.state}</b> · Success: {pingResult.successCount ?? '—'} · Fail: {pingResult.failureCount ?? '—'} · Avg: {pingResult.avgResponseTime ?? '—'} ms
            </p>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body" style={{ padding: 14 }}>
          <h4 style={{ fontSize: '.85rem', marginBottom: 10 }}><Terminal size={13} /> Set TR-069 Parameter</h4>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Parameter path</label>
              <input className="form-control font-mono" style={{ fontSize: '.72rem' }} value={paramName} onChange={(e) => setParamName(e.target.value)}
                placeholder="InternetGatewayDevice.…" />
            </div>
            <div className="form-group">
              <label className="form-label">Value</label>
              <input className="form-control" value={paramValue} onChange={(e) => setParamValue(e.target.value)} />
            </div>
          </div>
          <button className="btn btn-secondary" disabled={!paramName || busy === 'param'}
            onClick={() => run('param', () => acsAPI.setParams(device.serial, [[paramName, paramValue]]), 'Parameter set').then(onDone)}>
            Apply Parameter
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body" style={{ padding: 14 }}>
          <h4 style={{ fontSize: '.85rem', marginBottom: 10 }}><Download size={13} /> Firmware Upgrade</h4>
          <div className="form-group">
            <label className="form-label">Firmware image URL (reachable from the ONT)</label>
            <input className="form-control font-mono" style={{ fontSize: '.72rem' }} value={fwUrl} onChange={(e) => setFwUrl(e.target.value)}
              placeholder="http://<server>:7547/fw/firmware.bin" />
          </div>
          <button className="btn btn-secondary" disabled={!fwUrl || busy === 'fw'}
            onClick={() => { if (window.confirm(`Push firmware to ${device.serial}? Device will flash and reboot.`)) run('fw', () => acsAPI.firmware(device.serial, { url: fwUrl }), 'Firmware push started'); }}>
            Push Firmware
          </button>
        </div>
      </div>

      <div className="card" style={{ borderColor: 'var(--danger)' }}>
        <div className="card-body" style={{ padding: 14 }}>
          <h4 style={{ fontSize: '.85rem', marginBottom: 10, color: 'var(--danger)' }}><AlertTriangle size={13} /> Danger Zone</h4>
          <div className="flex" style={{ gap: 8 }}>
            <button className="btn btn-danger" disabled={busy === 'freset'}
              onClick={() => { if (window.confirm(`FACTORY RESET ${device.serial}?\n\nAll settings (WiFi, PPPoE) will be wiped. The ONT will need reconfiguration!`)) run('freset', () => acsAPI.factoryReset(device.serial), 'Factory reset sent'); }}>
              <AlertTriangle size={14} /> Factory Reset
            </button>
            <button className="btn btn-secondary" disabled={busy === 'del'}
              onClick={() => { if (window.confirm(`Remove ${device.serial} from the ACS database? It will re-register on next inform.`)) run('del', () => acsAPI.deleteDevice(device.serial), 'Removed from ACS').then(onClose); }}>
              Remove from ACS
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Device detail modal ──────────────────────────────────────────────────────
function DeviceModal({ serial, onClose }) {
  const [device, setDevice] = useState(null);
  const [tab, setTab] = useState('overview');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await acsAPI.getDevice(serial);
      setDevice(data.data);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load device'); onClose(); }
  }, [serial, onClose]);

  useEffect(() => { load(); }, [load]);

  const doRefresh = async () => {
    setBusy('refresh');
    try { await acsAPI.refresh(serial); toast.success('Device data refreshed'); await load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Refresh failed'); }
    finally { setBusy(''); }
  };

  const doReboot = async () => {
    if (!window.confirm(`Reboot ONT ${serial}? Customer internet will drop for 2-3 minutes.`)) return;
    setBusy('reboot');
    try {
      const { data } = await acsAPI.reboot(serial);
      toast.success(data.data.completed ? 'ONT is rebooting' : 'Reboot queued');
    } catch (err) { toast.error(err.response?.data?.message || 'Reboot failed'); }
    finally { setBusy(''); }
  };

  const TABS = [
    ['overview', 'Overview'], ['wifi', 'WiFi'], ['clients', 'Connected Devices'],
    ['tasks', 'Activity'], ['advanced', 'Advanced'],
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Router size={16} /> {serial}
              {device && <OnlineBadge online={device.online} />}
            </h3>
            {device?.customer && (
              <p className="text-muted text-small" style={{ marginTop: 2 }}>
                {device.customer.name} · {device.customer.customerId} · {device.customer.mobile}
              </p>
            )}
          </div>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ padding: '10px 20px 0', display: 'flex', gap: 6, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
          {TABS.map(([k, label]) => (
            <button key={k} className={`btn ${tab === k ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '5px 12px', fontSize: '.75rem', marginBottom: 8 }} onClick={() => setTab(k)}>
              {label}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <button className="btn btn-secondary" style={{ padding: '5px 12px', fontSize: '.75rem', marginBottom: 8 }} disabled={busy === 'refresh'} onClick={doRefresh}>
            <RefreshCw size={12} /> {busy === 'refresh' ? 'Refreshing…' : 'Refresh'}
          </button>
          <button className="btn btn-danger" style={{ padding: '5px 12px', fontSize: '.75rem', marginBottom: 8 }} disabled={busy === 'reboot'} onClick={doReboot}>
            <Power size={12} /> {busy === 'reboot' ? 'Rebooting…' : 'Reboot'}
          </button>
        </div>

        <div className="modal-body" style={{ overflowY: 'auto' }}>
          {!device ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : tab === 'overview' ? (
            <div>
              <div className="stats-grid" style={{ marginBottom: 14 }}>
                <div className="stat-card"><div className="stat-info"><p>Uptime</p><h3 style={{ fontSize: '1.05rem' }}>{fmtUptime(device.upTime)}</h3></div></div>
                <div className="stat-card"><div className="stat-info"><p>Optical RX / TX</p><h3 style={{ fontSize: '1.05rem' }}>{device.optical?.rx ?? '—'} / {device.optical?.tx ?? '—'}</h3><span className="trend">dBm</span></div></div>
                <div className="stat-card"><div className="stat-info"><p>Active Clients</p><h3 style={{ fontSize: '1.05rem' }}>{(device.hosts || []).filter((h) => h.active).length}</h3></div></div>
              </div>
              <table style={{ fontSize: '.8rem' }}>
                <tbody>
                  <tr><td className="text-muted" style={{ width: 170 }}>Model</td><td>{device.manufacturer} {device.model}</td></tr>
                  <tr><td className="text-muted">Firmware</td><td className="font-mono" style={{ fontSize: '.75rem' }}>{device.swVersion || '—'} (hw {device.hwVersion || '—'})</td></tr>
                  <tr><td className="text-muted">WAN IP</td><td className="font-mono" style={{ fontSize: '.75rem' }}>{device.ip || '—'}</td></tr>
                  <tr><td className="text-muted">Last inform</td><td>{fmtTime(device.lastInform)} (every {device.informInterval}s)</td></tr>
                  <tr><td className="text-muted">Last reboot</td><td>{fmtTime(device.lastBoot)}</td></tr>
                  <tr><td className="text-muted">Registered</td><td>{fmtTime(device.registeredAt)} · {device.informCount} informs</td></tr>
                  <tr><td className="text-muted">Last events</td><td className="font-mono" style={{ fontSize: '.72rem' }}>{(device.lastEvents || []).join(', ') || '—'}</td></tr>
                  <tr><td className="text-muted">Connection request</td><td className="font-mono" style={{ fontSize: '.72rem' }}>{device.connReqUrl || '—'}</td></tr>
                  <tr><td className="text-muted">ERP status</td><td><span className="badge badge-info">{device.erpStatus}</span></td></tr>
                </tbody>
              </table>
            </div>
          ) : tab === 'wifi' ? (
            <WifiTab device={device} onDone={load} />
          ) : tab === 'clients' ? (
            (device.hosts || []).length === 0 ? (
              <div className="empty-state"><Users size={28} /><h3>No client data</h3><p>Refresh to read the ONT's host table.</p></div>
            ) : (
              <div className="table-container">
                <table>
                  <thead><tr><th>Device Name</th><th>IP</th><th>MAC</th><th>Link</th><th>Status</th></tr></thead>
                  <tbody>
                    {device.hosts.map((h, i) => (
                      <tr key={i}>
                        <td>{h.hostname || 'Unknown'}</td>
                        <td className="font-mono" style={{ fontSize: '.75rem' }}>{h.ip}</td>
                        <td className="font-mono" style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{h.mac}</td>
                        <td><span className={`badge badge-${h.interface === 'wifi' ? 'catv' : 'info'}`}>{h.interface}</span></td>
                        <td><span className={`badge badge-${h.active ? 'success' : 'suspended'}`}>{h.active ? 'Active' : 'Inactive'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : tab === 'tasks' ? (
            <div className="table-container">
              <table>
                <thead><tr><th>Time</th><th>Action</th><th>Status</th></tr></thead>
                <tbody>
                  {(device.tasks || []).map((t) => (
                    <tr key={t.id}>
                      <td style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{fmtTime(t.createdAt)}</td>
                      <td style={{ fontSize: '.8rem' }}>{t.label}{t.auto && <span className="text-muted text-small"> (auto)</span>}</td>
                      <td>
                        <span className={`badge badge-${t.status === 'done' ? 'success' : t.status === 'fault' ? 'danger' : 'warning'}`}>{t.status}</span>
                        {t.fault && <div className="text-small" style={{ color: 'var(--danger)' }}>{t.fault.faultCode}: {t.fault.faultString}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <AdvancedTab device={device} onDone={load} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ACSPage() {
  const [status, setStatus] = useState(null);
  const [devices, setDevices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: '', online: '' });
  const [selected, setSelected] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const [s, d] = await Promise.all([
        acsAPI.getStatus(),
        acsAPI.getDevices({ q: filters.q || undefined, online: filters.online || undefined, limit: 100 }),
      ]);
      setStatus(s.data.data);
      setDevices(d.data.data);
      setTotal(d.data.total);
    } catch (err) { if (!silent) toast.error(err.response?.data?.message || 'Failed to reach ACS'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    timer.current = setInterval(() => load(true), 30000); // live view
    return () => clearInterval(timer.current);
  }, [load]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>ONT Control — TR-069 ACS</h2>
          <p className="text-muted text-small">
            JagdishNet ACS server · {status?.connected === false
              ? <span style={{ color: 'var(--danger)', fontWeight: 600 }}>NOT CONNECTED</span>
              : `${total} ONTs registered`}
          </p>
        </div>
        <button className="btn btn-secondary" onClick={() => load()}><RefreshCw size={14} /> Reload</button>
      </div>

      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#dbeafe', color: '#1e40af' }}><Router size={18} /></div>
          <div className="stat-info"><p>Total ONTs</p><h3>{status?.total ?? '—'}</h3></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#d1fae5', color: '#065f46' }}><Signal size={18} /></div>
          <div className="stat-info"><p>Online</p><h3>{status?.online ?? '—'}</h3></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fee2e2', color: '#991b1b' }}><Power size={18} /></div>
          <div className="stat-info"><p>Offline</p><h3>{status?.offline ?? '—'}</h3></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fef3c7', color: '#92400e' }}><Clock size={18} /></div>
          <div className="stat-info"><p>Pending Tasks</p><h3>{status?.pendingTasks ?? '—'}</h3></div>
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search serial, model, IP, customer…"
            value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
        </div>
        <select className="form-control" style={{ width: 140 }} value={filters.online}
          onChange={(e) => setFilters((f) => ({ ...f, online: e.target.value }))}>
          <option value="">All ONTs</option>
          <option value="true">Online</option>
          <option value="false">Offline</option>
        </select>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : devices.length === 0 ? (
            <div className="empty-state">
              <Router size={32} /><h3>No ONTs registered yet</h3>
              <p>Point your ONTs' TR-069 URL to this server (see docs/ACS-SETUP.md) and they will appear here automatically.</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr><th>Serial / Model</th><th>Customer</th><th>WiFi Networks</th><th>WAN IP</th><th>Uptime</th><th>Status</th><th>Last Seen</th></tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id} onClick={() => setSelected(d.serial)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className="font-mono" style={{ fontSize: '.8rem', fontWeight: 600 }}>{d.serial}</div>
                      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{d.manufacturer} {d.model}</div>
                    </td>
                    <td style={{ fontSize: '.8rem' }}>
                      {d.customer
                        ? <><div style={{ fontWeight: 500 }}>{d.customer.name}</div><div style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>{d.customer.customerId} · {d.customer.area || ''}</div></>
                        : <span className="badge badge-warning" style={{ fontSize: '.65rem' }}>not linked</span>}
                    </td>
                    <td style={{ fontSize: '.75rem' }}>
                      {(d.wifi || []).map((w) => (
                        <div key={w.instance}><span className="text-muted">{w.band}G:</span> {w.ssid} {w.clients > 0 && <span className="text-muted">({w.clients})</span>}</div>
                      ))}
                    </td>
                    <td className="font-mono" style={{ fontSize: '.75rem' }}>{d.ip || '—'}</td>
                    <td style={{ fontSize: '.78rem' }}>{fmtUptime(d.upTime)}</td>
                    <td><OnlineBadge online={d.online} /></td>
                    <td style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{d.lastInform ? format(new Date(d.lastInform), 'dd/MM HH:mm') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selected && <DeviceModal serial={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
